// Planning · Bord: een kolom per persoon, groepen per klant of bucket, de lijsten Actief, Geparkeerd en Afsluiten als
// tabbladen in de kolomkop, filters op label en zoektekst (de klant komt uit de bovenbalk), en kaarten slepen naar een
// collega. De klantkop in een kolom blijft bovenaan plakken bij het scrollen (app.css, .p-grp).
// Fases uit Planner staan als fasekaart met de taken erin (components/FaseCard.tsx, regels in lib/fases.ts); de kop
// van een fasekaart opent de faselade. Zoeken vindt een taak ook op elke fase erboven.
// Medewerker: de eigen kolom eerst, afsluiten alleen in de eigen kolom, slepen alleen van Zonder eigenaar naar de
// eigen kolom (zelf oppakken). Zonder Project-licentie (me.canWrite) geen slepen en afsluiten: dat gebeurt in Planner,
// via de knop in de taaklade.
// Uit Clockify: in de kolomkop per tabblad de som van de nog te presteren uren op de taken in dat tabblad
// (components/ColumnTabs.tsx), dezelfde sommen in Personen, en een groene stip als die persoon nu een timer heeft
// lopen; op de kaarten de uren en wie eraan bezig is. Lukt Clockify niet, dan zonder uren. Een medewerker ziet de
// uren alleen als Instellingen "bezetting zien" toelaat, zoals de bezetting elders.
// Zoektekst, label, groepering en de gekozen personen onthoudt de browser (lib/prefs.ts), net als de klant bovenaan.
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { fmtSince, useClockifyLive, type Timer } from "../lib/api-clockify";
import { NO_TEAM, teamOfPerson, useTeams } from "../lib/api-teams";
import { buildColumn, catOf, labelColor, type Column, type GroupBy } from "../lib/board";
import { faseBlocks, faseChain, padText, tasksByFase } from "../lib/fases";
import { useCardDrag, type DropTarget } from "../lib/drag";
import { useIndex, useMe, usePlanning, useTaskOps } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { isBoolMap, isStr, isStrOrNull, oneOf, usePref } from "../lib/prefs";
import { rightsOf, useSettings } from "../lib/settings";
import type { PlanningData, Task, TaskOp } from "../lib/types";
import { clockifyReady, TaskCard } from "../components/TaskCard";
import { ColumnTabs, tabSum, type ColumnView, type TabSum } from "../components/ColumnTabs";
import { FaseCard } from "../components/FaseCard";
import { TaskDrawer } from "../components/TaskDrawer";
import { Av } from "../components/Avatar";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";
import { useToast } from "../shell/Toast";
import { PlanningTabs } from "./PlanningTabs";

export function Bord() {
  const { data, error, refetch } = usePlanning();
  const { data: me } = useMe();
  const { data: teamsData } = useTeams();
  const { data: live } = useClockifyLive();
  const settings = useSettings().data?.settings;
  const idx = useIndex(data);
  const { klant } = useAppState();
  const ops = useTaskOps();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  // zoektekst, label, groepering en personen onthoudt de browser: ze blijven staan bij een ander tabblad of herladen
  const [q, setQ] = usePref<string>("tp.bord.zoek", "", isStr);
  const [labelF, setLabelF] = usePref<string | null>("tp.bord.label", null, isStrOrNull);
  const [groupBy, setGroupBy] = usePref<GroupBy>("tp.bord.groep", "klant", oneOf("klant", "bucket"));
  const [hidden, setHidden] = usePref<Record<string, boolean>>("tp.bord.personen", {}, isBoolMap);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [views, setViews] = useState<Record<string, ColumnView>>({});
  const [pp, setPp] = useState(false);
  const [lbl, setLbl] = useState(false);
  const sel = params.get("taak");
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const names = useMemo(() => ({
    klant: (id: string) => idx.klanten.get(id)?.name || "",
    bucket: (id: string) => idx.buckets.get(id)?.name || "(geen bucket)",
  }), [idx]);
  // per fase alle open taken, ook die in subfases: voor "n bij collega's" op een fasekaart
  const byFase = useMemo(() => tasksByFase(data?.tasks || [], idx.fases), [data, idx]);

  const drag = useCardDrag((taskId, fromKey, to) => onDrop(taskId, fromKey, to));
  if (!data && error) return <LoadError what="De planningsdata" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Planningsdata laden…</div>;
  const med = me.role === "medewerker";
  const write = me.canWrite;

  const qq = q.trim().toLowerCase();
  const visible = (t: Task) => {
    if (klant && t.projectId !== klant) return false;
    const labs = t.labels.map((l) => l.name.toLowerCase());
    if (labelF === "__none" ? labs.length > 0 : labelF && !labs.includes(labelF.toLowerCase())) return false;
    return !qq || [t.title, names.klant(t.projectId), names.bucket(t.bucketId), padText(faseChain(t.faseId, idx.fases))].join(" ").toLowerCase().includes(qq);
  };
  const tasks = data.tasks.filter(visible);
  const filtered = !!(klant || labelF || qq);
  const act = tasks.filter((t) => catOf(t) === "actief");
  const actCount = (pid: string | null) => act.filter((t) => (pid ? t.owners.some((o) => o.personId === pid) : !t.owners.length)).length;

  // volgorde: planner eerst wat nog geen eigenaar heeft; medewerker eerst de eigen kolom, Zonder eigenaar achteraan.
  // Daartussen per team (zoals in Teams ingericht, Zonder team achteraan), binnen een team op aantal actieve taken.
  const persons = [...data.people].sort((a, b) => actCount(b.id) - actCount(a.id) || a.name.localeCompare(b.name));
  const teams = teamsData?.teams || [];
  const teamOf = teamOfPerson(teams);
  const byTeam = [...teams.map((t) => ({ id: t.id as number | null, name: t.name })), { id: null, name: NO_TEAM }]
    .map((g) => ({ ...g, ids: persons.filter((p) => (teamOf.get(p.id)?.id ?? null) === g.id).map((p) => p.id) })).filter((g) => g.ids.length);
  const myId = med ? me.id : null;
  const rest = byTeam.flatMap((g) => g.ids).filter((id) => id !== myId);
  const order: (string | null)[] = med ? [me.id, ...rest, null] : [null, ...rest];
  const key = (pid: string | null) => pid || "__none";
  const shown = order.filter((pid) => !hidden[key(pid)]);
  // Personen: alleen één team tonen; de koppen tussen de mensen alleen als er teams zijn
  const teamPicks = byTeam.filter((g) => g.id !== null);
  const onlyTeam = (ids: string[]) => setHidden(Object.fromEntries(order.filter((pid) => !pid || !ids.includes(pid)).map((pid) => [key(pid), true])));
  const teamShown = teamPicks.find((g) => shown.length === g.ids.length && g.ids.every((id) => !hidden[key(id)]));
  const headOf = (pid: string | null) => {
    if (med && pid === me.id) return "Uw kolom";
    if (med && !pid) return "Nog toe te wijzen";
    if (!teams.length || !pid) return null;
    const g = byTeam.find((x) => x.ids.filter((id) => id !== myId)[0] === pid);
    if (!g) return null;
    const listed = g.ids.filter((id) => id !== myId).length;
    return g.name + " · " + (listed === g.ids.length ? listed : listed === 1 ? "1 collega" : listed + " collega's");
  };
  // elke kolom één keer opbouwen: voor het bord (de gekozen personen) en voor de sommen in Personen (iedereen)
  const colOf = new Map(order.map((pid) => [key(pid), buildColumn(pid, tasks, data, groupBy, names)] as const));
  const columns = shown.map((pid) => colOf.get(key(pid))!).filter((c) => !filtered || c.act.length + c.parked.length + c.closing.length > 0);

  // Clockify: de uren per taak voor de sommen in de tabbladen en in Personen (een medewerker alleen als Instellingen dat
  // toelaat, zoals de bezetting elders), en de lopende timer per persoon
  const hours = live && clockifyReady(live.clockify) && (!med || rightsOf(settings).seeCapacity) ? live.tasks : null;
  const sumCell = (s: TabSum) => (!s.n ? <span className="t-mute">–</span> : s.withEst ? <>{Math.round(s.hours)} u</> : <span className="t-mute">? u</span>);
  const timerOf = (pid: string | null) => (pid && live && clockifyReady(live.clockify) ? live.timers.find((t) => t.personId === pid) : undefined);
  const timerTitle = (t: Timer) => "Timer loopt" + (t.taskName || t.description ? " op " + (t.taskName || t.description) : "") + " · " + fmtSince(t.since) + (t.stale ? " · wellicht vergeten" : "");

  const openTask = (id: string) => { if (drag.suppressClick.current) return; setParams((p) => { p.delete("fase"); p.set("taak", id); return p; }); };
  const openFase = (id: string) => setParams((p) => { p.delete("taak"); p.set("fase", id); return p; });
  const closeTask = () => setParams((p) => { p.delete("taak"); return p; });

  function onDrop(taskId: string, fromKey: string, to: DropTarget) {
    const d = dataRef.current as PlanningData;
    const t = d.tasks.find((x) => x.id === taskId);
    if (!t || !to.colKey) return;
    const from = fromKey === "__none" ? null : fromKey, dest = to.colKey === "__none" ? null : to.colKey;
    const same = to.colKey === fromKey;
    // een taak in een fase blijft in de bucket van haar fase: slepen wijzigt dan enkel de eigenaar
    const bucket = !med && groupBy === "bucket" && to.groupLabel && !t.faseId ? d.buckets.find((b) => b.projectId === t.projectId && b.name === to.groupLabel) : null;
    const newBucket = bucket && bucket.id !== t.bucketId ? bucket : null;
    if (same && !newBucket) return;
    if (med) {
      if (!t.owners.length && dest === me!.id) ops.run(t, [{ kind: "owner", personId: me!.id, on: true }], "U bent nu eigenaar van " + t.title).catch(() => {});
      else toast.flash("Een taak zonder eigenaar sleept u naar uw eigen kolom; toewijzen aan een collega doet de planner");
      return;
    }
    const o: TaskOp[] = [];
    if (!same) {
      if (from) o.push({ kind: "owner", personId: from, on: false });
      if (dest && !t.owners.some((x) => x.personId === dest)) o.push({ kind: "owner", personId: dest, on: true });
    }
    if (newBucket) o.push({ kind: "bucket", bucketId: newBucket.id });
    if (!o.length) return;
    const who = dest ? idx.people.get(dest)?.first : null;
    const parts = [!same ? (dest ? "naar " + who : "eigenaar weggehaald") : "", newBucket ? "bucket " + newBucket.name : ""].filter(Boolean).join(", ");
    ops.run(t, o, parts.charAt(0).toUpperCase() + parts.slice(1) + ": " + t.title).catch(() => {});
  }

  const canDrag = (t: Task) => write && (!med || !t.owners.length);
  const closeAll = (c: Column) => ops.runMany(c.closing.map((t) => ({ task: t, ops: [{ kind: "progress", progress: 1 } as TaskOp] })), c.closing.length === 1 ? "1 taak afgesloten in Planner" : c.closing.length + " taken afgesloten in Planner");
  const selTask = sel ? data.tasks.find((t) => t.id === sel) : null;

  const labelOpts = [{ label: "Alle labels", v: null as string | null }, ...[...new Set(data.labels.map((l) => l.name))].sort().map((n) => ({ label: n, v: n as string | null })), { label: "Zonder label", v: "__none" }];
  const countFor = (v: string | null) => data.tasks.filter((t) => (!klant || t.projectId === klant) && (v === null || (v === "__none" ? !t.labels.length : t.labels.some((l) => l.name.toLowerCase() === v.toLowerCase())))).length;

  return (
    <>
      <div className="p-head">
        <div><h1>Planning</h1></div>
        <div className="p-tools"><label className="ex-search p-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek taak, klant of route" aria-label="Zoek taak, klant of route" value={q} onChange={(e) => setQ(e.target.value)} /></label></div>
      </div>
      <div className="p-tabrow">
        <PlanningTabs current="bord" />
        <div className="p-tools">
          <div className="p-ddwrap">
            <button type="button" className="ex-btn p-dd" aria-haspopup="true" aria-expanded={pp} onClick={() => { setPp(!pp); setLbl(false); }}>Personen<span className="p-dd-v">{shown.length === order.length ? "alle" : shown.length === 0 ? "geen" : teamShown ? "team " + teamShown.name : shown.length + " van " + order.length}</span><Icon name="down" size={14} /></button>
            {pp && <>
              <button type="button" className="p-dd-scrim" aria-label="Personen sluiten" onClick={() => setPp(false)} />
              <div className="p-pop" role="dialog" aria-label="Personen op het bord">
                <div className="p-pop-h"><span>Kolommen op het bord</span><span className="p-pop-acts"><button type="button" className="ex-link" onClick={() => setHidden({})}>Iedereen</button><button type="button" className="ex-link" onClick={() => setHidden(Object.fromEntries(order.map((pid) => [key(pid), true])))}>Wissen</button></span></div>
                {teamPicks.length > 0 && (
                  <div className="p-pop-teams"><span>Alleen team</span>
                    {teamPicks.map((g) => <button key={g.id} type="button" className={"p-tog p-tog-s" + (teamShown === g ? " on" : "")} onClick={() => onlyTeam(g.ids)}>{g.name}</button>)}
                  </div>
                )}
                <div className="p-pop-cols" aria-hidden="true"><span /><span /><span>Persoon</span><span>Taken</span>{hours && <><span>Actief</span><span>Geparkeerd</span></>}</div>
                <div className="p-pop-b">
                  {order.map((pid) => {
                    const p = pid ? idx.people.get(pid) || (pid === me.id ? { name: me.name, initials: me.initials } : null) : null, on = !hidden[key(pid)];
                    const head = headOf(pid), tm = timerOf(pid), col = colOf.get(key(pid));
                    return (
                      <Fragment key={key(pid)}>
                        {head && <div className="p-pop-g">{head}</div>}
                        <label className={"p-opt" + (on ? " on" : "")}>
                          <input type="checkbox" checked={on} onChange={() => setHidden({ ...hidden, [key(pid)]: on })} />
                          {p ? <span className="p-avwrap"><Av id={pid} init={p.initials} cls="p-av s" />{tm && <span className="st bezig" title={timerTitle(tm)} />}</span> : <span className="p-av none s"><Icon name="userx" size={12} /></span>}
                          <span className="nm">{p ? p.name : "Zonder eigenaar"}</span><span className="c">{actCount(pid)}</span>
                          <span className="h">{hours && col && sumCell(tabSum(col.act, hours))}</span>
                          <span className="h">{hours && col && sumCell(tabSum(col.parked, hours))}</span>
                        </label>
                      </Fragment>
                    );
                  })}
                </div>
                <div className="p-pop-f">{hours ? "Actieve taken · nog te presteren uren, actief en geparkeerd" : "Actieve taken per persoon"} · {med ? "teams richt de planner in" : <Link className="ex-link" to="/teams">Teams inrichten</Link>}</div>
              </div>
            </>}
          </div>
          <div className="p-ddwrap">
            <button type="button" className="ex-btn p-dd" aria-haspopup="listbox" aria-expanded={lbl} onClick={() => { setLbl(!lbl); setPp(false); }}>Label<span className="p-dd-v">{labelF === "__none" ? "geen" : labelF || "alle"}</span><Icon name="down" size={14} /></button>
            {lbl && <>
              <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setLbl(false)} />
              <div className="p-pop p-pop-s" role="listbox" aria-label="Label kiezen">
                <div className="p-pop-b">
                  {labelOpts.map((o) => (
                    <button key={o.label} type="button" className={"p-opt1" + (labelF === o.v ? " on" : "")} onClick={() => { setLabelF(o.v); setLbl(false); }}>
                      <span className={"p-radio" + (labelF === o.v ? " on" : "")} />
                      {o.v && o.v !== "__none" && <i className="p-lbl-dot" style={{ background: labelColor(o.v) }} />}
                      <span className="nm">{o.label}</span><span className="c">{countFor(o.v)}</span>
                    </button>
                  ))}
                </div>
                <div className="p-pop-f">Labels uit Planner · aantal open taken</div>
              </div>
            </>}
          </div>
          <div className="ex-seg" role="group" aria-label="Groeperen">
            <button type="button" aria-pressed={groupBy === "klant"} onClick={() => setGroupBy("klant")}>Per klant</button>
            <button type="button" aria-pressed={groupBy === "bucket"} onClick={() => setGroupBy("bucket")}>Per bucket</button>
          </div>
        </div>
      </div>
      {filtered && (
        <div className="p-fnote"><Icon name="search" size={14} />
          <span>Gefilterd: {[klant ? "klant " + names.klant(klant) : "", labelF ? "label " + (labelF === "__none" ? "geen" : labelF) : "", qq ? "zoek “" + q.trim() + "”" : ""].filter(Boolean).join(" · ")}</span>
          <button type="button" className="ex-link" onClick={() => { setLabelF(null); setQ(""); }}>Filters wissen</button>
        </div>
      )}
      <div className="p-cols">
        {columns.map((c) => {
          const p = c.personId ? idx.people.get(c.personId) || (c.personId === me.id ? { name: me.name, initials: me.initials } : null) : null;
          const view = views[c.key] || "actief";
          const setView = (v: ColumnView) => setViews({ ...views, [c.key]: v });
          const canCloseCol = write && (!med || c.personId === me.id);
          const tm = timerOf(c.personId);
          // de tip op de kop van Zonder eigenaar: hoe een kaart een eigenaar krijgt
          const hint = p ? undefined : !write ? (med ? "Open een kaart om ze in Planner op te pakken" : "Toewijzen doet u in Planner") : med ? "Sleep een kaart naar uw kolom" : "Sleep een kaart naar een collega";
          const card = (t: Task, inGroup: boolean) => (
            <TaskCard key={t.id} task={t} data={data} inGroup={inGroup} klantName={names.klant(t.projectId)} draggable={canDrag(t)} live={live} meId={me.id} onOpen={() => openTask(t.id)} onPointerDown={canDrag(t) ? drag.start(t.id, c.key) : undefined} />
          );
          // losse taken en fasekaarten; in Geparkeerd en Afsluiten (een lijst zonder klantgroepen) de klant in de grijze regel
          const fk = c.key + "|fase|";
          const blocks = (ts: Task[], list: boolean) => faseBlocks(ts, idx.fases, list ? { prefix: (t) => names.klant(t.projectId) } : {}).map((b) => (b.kind === "task" ? card(b.task, !list) : (
            <FaseCard key={b.fase.id} block={b} today={data.today} renderTask={(t) => card(t, true)} onOpenFase={openFase}
              isOpen={(id) => open[fk + id] !== false} onToggle={(id) => setOpen((o) => ({ ...o, [fk + id]: o[fk + id] === false }))}
              others={(id) => (byFase.get(id) || []).filter((t) => (c.personId ? !t.owners.some((o) => o.personId === c.personId) : t.owners.length > 0)).length} />
          )));
          return (
            <section key={c.key} className="p-col" data-col={c.key}>
              <div className="p-col-h" title={hint}>
                <div className="r1">
                  {p ? <span className="p-avwrap"><Av id={c.personId} init={p.initials} cls="p-av l" />{tm && <span className="st bezig" title={timerTitle(tm)} />}</span> : <span className="p-av none l"><Icon name="userx" size={14} /></span>}
                  <div style={{ minWidth: 0 }}><h2>{p ? p.name : "Zonder eigenaar"}</h2>{med && c.personId === me.id && <p>Uw kolom</p>}</div>
                </div>
                <ColumnTabs view={view} onView={setView} who={p ? "van " + p.name.split(" ")[0] : "zonder eigenaar"} act={c.act} parked={c.parked} closing={c.closing} hours={hours} />
              </div>
              <div className="p-col-b">
                {view === "actief" && <>
                  {c.groups.map((g, i) => {
                    const gk = c.key + "|" + groupBy + "|" + g.label, isOpen = gk in open ? open[gk] : i < 2 || filtered;
                    return (
                      <Fragment key={gk}>
                        <button type="button" className="p-grp" data-label={g.label} aria-expanded={isOpen} onClick={() => setOpen({ ...open, [gk]: !isOpen })}>
                          <span><Icon name={isOpen ? "down" : "right"} size={14} />{g.label}</span><span>{g.tasks.length}</span>
                        </button>
                        {isOpen && blocks(g.tasks, false)}
                      </Fragment>
                    );
                  })}
                  {!c.act.length && <p className="p-empty">Geen actieve taken</p>}
                </>}
                {/* de lijsten zonder koptekst: het tabblad zegt al wat het is */}
                {view === "geparkeerd" && <>
                  {!c.parked.length && <p className="p-empty">Niets geparkeerd</p>}
                  {blocks(c.parked, true)}
                </>}
                {view === "afsluiten" && <>
                  {c.closing.length > 0 && canCloseCol && <button type="button" className="ex-btn-xs ex-btn-xs--primary p-closeall" onClick={() => closeAll(c)}><Icon name="check" size={13} />{c.closing.length === 1 ? "Deze taak afsluiten" : "Alle " + c.closing.length + " afsluiten"}</button>}
                  {!c.closing.length && <p className="p-empty">Niets af te sluiten</p>}
                  {blocks(c.closing, true)}
                </>}
              </div>
            </section>
          );
        })}
        {!shown.length && <p className="p-empty p-nocols">Niemand geselecteerd. Kies via Personen wie u op het bord wilt zien.</p>}
        {shown.length > 0 && !columns.length && <p className="p-empty p-nocols">Geen taken met deze filters. Wis de filters of kies een andere klant.</p>}
      </div>
      {selTask && <TaskDrawer key={selTask.id} task={selTask} data={data} onClose={closeTask} />}
    </>
  );
}
